import { useTranslation } from "react-i18next";
import { GoMakeDialog, GomakeTextInput, GomakePrimaryButton } from "@/components";
import { useStyle } from "./style";
import { useAddProductWithAI } from "./use-add-product-with-ai";
import {
  BatchAnalyzingModal,
  confirmBatchAnalyzingAndStartCalculating,
  markBatchAnalyzingPending,
} from "./batch-analyzing-modal";
import { Stack, Typography } from "@mui/material";
import { UploadNewIcon } from "@/icons";
import { useRef, useState, useEffect } from "react";
import { useGomakeAxios, useGomakeRouter, useSnackBar } from "@/hooks";
import { PermissionCheck } from "@/components/CheckPermission/check-permission";
import { Permissions } from "@/components/CheckPermission/enum";
import { useUserPermission } from "@/hooks/use-permission";
import { checkAiCreditsGateApi } from "@/services/api-service/ai-credits/ai-credits-api";
import { AiCreditsRequiredModal } from "@/widgets/gomake-ai-widget/components/ai-credits-required-modal";

type CreditGateBlock = {
  blocked: boolean;
  message: string | null;
  neverLoaded: boolean;
};

async function readCreditGate(callApi: any): Promise<CreditGateBlock> {
  const openGate: CreditGateBlock = {
    blocked: false,
    message: null,
    neverLoaded: false,
  };
  try {
    const gateRes: any = await checkAiCreditsGateApi(callApi, () => {});
    const gatePayload =
      gateRes?.allowed != null
        ? gateRes
        : gateRes?.data?.allowed != null
          ? gateRes.data
          : gateRes?.data?.data;
    if (!gatePayload) return openGate;
    const message =
      typeof gatePayload.message === "string" ? gatePayload.message : null;
    const neverLoaded =
      typeof message === "string" &&
      message.toLowerCase().includes("no ai credits loaded");
    const blocked =
      gatePayload.allowed === false ||
      (typeof gatePayload.balance === "number" && gatePayload.balance <= 0);
    return { blocked, message, neverLoaded };
  } catch {
    return openGate;
  }
}

interface AddProductWithAIModalProps {
  open: boolean;
  onClose: () => void;
  quoteId?: string;
  ensureQuoteId?: () => Promise<string | null | undefined>;
  onSuccess?: () => void;
  navigateToQuoteOnSuccess?: boolean;
  /** Called when batch analyzing confirmation should show without navigating away */
  onBatchAnalyzingStarted?: () => void;
}

const AddProductWithAIModal = ({
  open,
  onClose,
  quoteId,
  ensureQuoteId,
  onSuccess,
  navigateToQuoteOnSuccess = false,
  onBatchAnalyzingStarted,
}: AddProductWithAIModalProps) => {
  const { classes } = useStyle();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const parametersFileInputRef = useRef<HTMLInputElement>(null);
  const batchFileInputRef = useRef<HTMLInputElement>(null);
  const { navigate } = useGomakeRouter();
  const { callApi } = useGomakeAxios();
  const { alertFault } = useSnackBar();
  const { CheckPermission } = useUserPermission();
  const canUploadParametersFile = CheckPermission(
    Permissions.SHOW_UPLOAD_AI_PARAMETERS_FILE
  );
  const [showAnalyzingModal, setShowAnalyzingModal] = useState(false);
  const [isStartingBatch, setIsStartingBatch] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditsAllowed, setCreditsAllowed] = useState(false);
  const [creditsMessage, setCreditsMessage] = useState<string | null>(null);
  const [creditsNeverLoaded, setCreditsNeverLoaded] = useState(false);

  const blockForCredits = (result: CreditGateBlock) => {
    setCreditsMessage(result.message);
    setCreditsNeverLoaded(result.neverLoaded);
    setShowCreditsModal(true);
    setCreditsAllowed(false);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setCreditsAllowed(false);
      return;
    }

    let cancelled = false;
    void readCreditGate(callApi).then((result) => {
      if (cancelled) return;
      if (result.blocked) {
        blockForCredits(result);
        return;
      }
      setCreditsAllowed(true);
    });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const navigateToQuoteConfirmation = (idOverride?: string | null) => {
    // Prefer the freshly resolved/created quote id — prop quoteId can still be
    // empty right after ensureQuoteId() (batch / Job Basket navigate immediately).
    const id = (idOverride || quoteId)?.trim();
    if (id) {
      navigate(`/quote?Id=${encodeURIComponent(id)}`);
    } else {
      navigate("/quote");
    }
  };

  const {
    prompt,
    setPrompt,
    isProcessing,
    handleSubmitPrompt,
    handleJobsFileSelect,
    handleParametersFileSelect,
    handleBatchFileSelect,
    selectedFileName,
    selectedParametersFileName,
    selectedBatchFileName,
    selectedFile,
    batchFile,
    resolveQuoteId,
    sendBatchFileToAI,
    sendJobsExcelToAI,
    clearBatchSelection,
    clearJobsSelection,
  } = useAddProductWithAI({
    quoteId,
    ensureQuoteId,
    onSuccess: () => {
      // Quote updates via polling / UpdateQuoteItems
    },
  });

  const clearOtherFileInputs = (keep: "excel" | "parameters" | "batch") => {
    if (keep !== "excel" && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (keep !== "parameters" && parametersFileInputRef.current) {
      parametersFileInputRef.current.value = "";
    }
    if (keep !== "batch" && batchFileInputRef.current) {
      batchFileInputRef.current.value = "";
    }
  };

  const startAsyncUploadUx = (
    resolvedQuoteId: string,
    options?: { callOnSuccess?: boolean }
  ) => {
    onClose();
    markBatchAnalyzingPending();
    if (navigateToQuoteOnSuccess) {
      navigateToQuoteConfirmation(resolvedQuoteId);
    } else if (onBatchAnalyzingStarted) {
      onBatchAnalyzingStarted();
    } else {
      setShowAnalyzingModal(true);
    }
    // Job Basket skips this — getQuote before AddItem loads an empty document and
    // can wipe quote state / leave isNewCreation path active. Parameters Excel
    // still calls onSuccess only after HTTP completes (see handlePrimaryAction).
    if (options?.callOnSuccess !== false && onSuccess) {
      onSuccess();
    }
  };

  const handleBatchPrimaryAction = async () => {
    if (!batchFile) {
      return;
    }

    setIsStartingBatch(true);
    try {
      const resolvedQuoteId = await resolveQuoteId();
      if (!resolvedQuoteId) {
        alertFault("No quote in create was found");
        return;
      }

      const fileToUpload = batchFile;

      // Close upload modal immediately — do not wait for OpenAI extract.
      clearBatchSelection();
      if (batchFileInputRef.current) {
        batchFileInputRef.current.value = "";
      }
      startAsyncUploadUx(resolvedQuoteId);

      // Fire-and-forget upload (backend accepts immediately, processes in background).
      void sendBatchFileToAI(fileToUpload);
    } finally {
      setIsStartingBatch(false);
    }
  };

  const handleJobsExcelPrimaryAction = async () => {
    if (!selectedFile) {
      return;
    }

    setIsStartingBatch(true);
    try {
      const resolvedQuoteId = await resolveQuoteId();
      if (!resolvedQuoteId) {
        alertFault("No quote in create was found");
        return;
      }

      const fileToUpload = selectedFile;

      // Close upload modal immediately — Agent 1 + AddItem run while user is on quote.
      clearJobsSelection();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      // Same UX as batch, but do NOT getQuote yet (items are not in ERP).
      startAsyncUploadUx(resolvedQuoteId, { callOnSuccess: false });

      // Mirror Parameters Excel: getQuote only after HTTP accept (rows already in ERP).
      // Navigate with the upload quoteId so getQuote does not stay on isNewCreation.
      void sendJobsExcelToAI(fileToUpload).then((result) => {
        if (!result?.success || !onSuccess) {
          return;
        }
        navigate(`/quote?Id=${encodeURIComponent(resolvedQuoteId)}`);
        window.setTimeout(() => onSuccess(), 500);
      });
    } finally {
      setIsStartingBatch(false);
    }
  };

  const handlePrimaryAction = async () => {
    const gate = await readCreditGate(callApi);
    if (gate.blocked) {
      blockForCredits(gate);
      return;
    }

    if (batchFile || selectedBatchFileName) {
      await handleBatchPrimaryAction();
      return;
    }

    if (selectedFile || selectedFileName) {
      await handleJobsExcelPrimaryAction();
      return;
    }

    const success = await handleSubmitPrompt();
    if (!success) {
      return;
    }

    onClose();
    if (navigateToQuoteOnSuccess) {
      navigateToQuoteConfirmation();
    }
    if (onSuccess) {
      onSuccess();
    }
  };

  const busy = isProcessing || isStartingBatch;

  return (
    <>
      <GoMakeDialog
        open={open && creditsAllowed}
        onClose={onClose}
        title={t("sales.quote.addProductWithAI")}
        showFooter={true}
        primaryButtonLabel={
          busy ? t("sales.quote.processing") : t("sales.quote.addProduct")
        }
        onPrimaryAction={handlePrimaryAction}
        primaryButtonDisabled={
          busy ||
          (!prompt.trim() &&
            !selectedFileName &&
            !selectedParametersFileName &&
            !selectedBatchFileName)
        }
        primaryButtonLoading={busy}
        maxWidth="sm"
      >
        <Stack spacing={3} style={classes.contentContainer}>
          <Typography variant="body1" style={classes.description}>
            {canUploadParametersFile
              ? t("sales.quote.addProductWithAIDescription")
              : t("sales.quote.addProductWithAIDescriptionNoParameters")}
          </Typography>

          <Stack spacing={2}>
            <Typography variant="subtitle2" style={classes.sectionTitle}>
              {t("sales.quote.enterPrompt")}
            </Typography>
            <GomakeTextInput
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t("sales.quote.promptPlaceholder")}
              multiline
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "auto",
                  minHeight: "100px",
                  alignItems: "flex-start",
                  paddingTop: "8px",
                },
                "& textarea": {
                  minHeight: "84px",
                },
              }}
              disabled={busy}
              style={classes.textInput}
            />
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            style={classes.dividerContainer}
          >
            <div style={classes.divider} />
            <Typography variant="body2" style={classes.dividerText}>
              {t("sales.quote.or")}
            </Typography>
            <div style={classes.divider} />
          </Stack>

          <Stack spacing={2}>
            <Typography variant="subtitle2" style={classes.sectionTitle}>
              {t("sales.quote.uploadJobBasketFile")}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.xml,.csv"
              onChange={(e) => {
                handleJobsFileSelect(e);
                clearOtherFileInputs("excel");
              }}
              style={{ display: "none" }}
              disabled={busy}
            />
            <GomakePrimaryButton
              variant="contained"
              leftIcon={
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    filter: "brightness(0) invert(1)",
                  }}
                >
                  <UploadNewIcon />
                </span>
              }
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              style={classes.uploadButton}
            >
              {selectedFileName || t("sales.quote.selectJobBasketFile")}
            </GomakePrimaryButton>
            {selectedFileName && (
              <Typography variant="caption" style={classes.fileName}>
                {selectedFileName}
              </Typography>
            )}
          </Stack>

          <PermissionCheck
            userPermission={Permissions.SHOW_UPLOAD_AI_PARAMETERS_FILE}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              style={classes.dividerContainer}
            >
              <div style={classes.divider} />
              <Typography variant="body2" style={classes.dividerText}>
                {t("sales.quote.or")}
              </Typography>
              <div style={classes.divider} />
            </Stack>

            <Stack spacing={2}>
              <Typography variant="subtitle2" style={classes.sectionTitle}>
                {t("sales.quote.uploadParametersFile")}
              </Typography>
              <input
                ref={parametersFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                  handleParametersFileSelect(e);
                  clearOtherFileInputs("parameters");
                }}
                style={{ display: "none" }}
                disabled={busy}
              />
              <GomakePrimaryButton
                variant="contained"
                leftIcon={
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      filter: "brightness(0) invert(1)",
                    }}
                  >
                    <UploadNewIcon />
                  </span>
                }
                onClick={() => parametersFileInputRef.current?.click()}
                disabled={busy}
                style={classes.uploadButton}
              >
                {selectedParametersFileName ||
                  t("sales.quote.selectParametersExcelFile")}
              </GomakePrimaryButton>
              {selectedParametersFileName && (
                <Typography variant="caption" style={classes.fileName}>
                  {selectedParametersFileName}
                </Typography>
              )}
            </Stack>
          </PermissionCheck>

          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            style={classes.dividerContainer}
          >
            <div style={classes.divider} />
            <Typography variant="body2" style={classes.dividerText}>
              {t("sales.quote.or")}
            </Typography>
            <div style={classes.divider} />
          </Stack>

          <Stack spacing={2}>
            <Typography variant="subtitle2" style={classes.sectionTitle}>
              {t("sales.quote.uploadBatchFile")}
            </Typography>
            <input
              ref={batchFileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,.txt,.doc,.docx,.pdf"
              onChange={(e) => {
                handleBatchFileSelect(e);
                clearOtherFileInputs("batch");
              }}
              style={{ display: "none" }}
              disabled={busy}
            />
            <GomakePrimaryButton
              variant="contained"
              leftIcon={
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    filter: "brightness(0) invert(1)",
                  }}
                >
                  <UploadNewIcon />
                </span>
              }
              onClick={() => batchFileInputRef.current?.click()}
              disabled={busy}
              style={classes.uploadButton}
            >
              {selectedBatchFileName || t("sales.quote.selectBatchFile")}
            </GomakePrimaryButton>
            {selectedBatchFileName && (
              <Typography variant="caption" style={classes.fileName}>
                {selectedBatchFileName}
              </Typography>
            )}
          </Stack>
        </Stack>
      </GoMakeDialog>

      <AiCreditsRequiredModal
        open={showCreditsModal}
        locked
        onClose={() => setShowCreditsModal(false)}
        message={creditsMessage}
        neverLoaded={creditsNeverLoaded}
      />
      <BatchAnalyzingModal
        open={showAnalyzingModal}
        onClose={() => {
          confirmBatchAnalyzingAndStartCalculating();
          setShowAnalyzingModal(false);
        }}
      />
    </>
  );
};

export { AddProductWithAIModal };
